/* eslint-disable react/prop-types */
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from 'components/common/FalconCardHeader';
import LeafletMap from './LeafletMap';
import { toPng } from 'html-to-image';
import cloudDownload from 'assets/img/icons/cloud-download.svg';
import { isEmpty } from 'lodash';
import Model from './Model';
import { FiInfo } from 'react-icons/fi';

const MultiYearMap = ({
  data,
  coordinates,
  legendData,
  type,
  info,
  variant,
  label
}) => {
  const sortYears = useCallback(years => years.sort((a, b) => b - a), []);

  const getSortedYears = useCallback(() => {
    if (Array.isArray(variant)) {
      return sortYears(variant);
    } else if (typeof variant === 'object') {
      return sortYears(Object.keys(variant));
    }
    return [];
  }, [variant, sortYears]);

  const sortedYears = useMemo(() => getSortedYears(), [getSortedYears]);
  const defaultYear = sortedYears[0] || '';

  // const defaultMonth = useMemo(
  //   () =>
  //     typeof variant === 'object' && Array.isArray(variant[defaultYear])
  //       ? variant[defaultYear][0]
  //       : null,
  //   [variant, defaultYear]
  // );

  // Determine the available months for the default year
  const getAvailableMonths = year => {
    return typeof variant === 'object' && variant[year] ? variant[year] : [];
  };

  const defaultMonth = useMemo(
    () => getAvailableMonths(defaultYear)[0] || null,
    [defaultYear, variant]
  );

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [showModal, setShowModal] = useState(false);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const targetElementRef = useRef(null);

  // Event handler for dropdown changes
  const handleDropdownChange = e => {
    const { name, value } = e.target;

    if (name === 'month') {
      setSelectedMonth(value);
    } else if (name === 'year') {
      setSelectedYear(value);
    }
  };

  const handleYearChange = useCallback(
    event => {
      const newYear = event.target.value;
      setSelectedYear(newYear);
      const availableMonths = variant[newYear];

      if (availableMonths.includes(selectedMonth)) {
        setSelectedMonth(selectedMonth);
      } else {
        const lastMonth = availableMonths[0] || '';
        setSelectedMonth(lastMonth);
      }
    },
    [variant, selectedMonth]
  );

  const handleMonthChange = useCallback(event => {
    console.log(event.target.value);
    setSelectedMonth(event.target.value);
  }, []);

  const onButtonClick = useCallback(async () => {
    if (targetElementRef.current === null) {
      return;
    }
    const title = label;
    const currentDate = new Date();

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hour = String(currentDate.getHours()).padStart(2, '0');
    const minute = String(currentDate.getMinutes()).padStart(2, '0');
    const second = String(currentDate.getSeconds()).padStart(2, '0');

    const timestamp = `${year}-${month}-${day}-${hour}-${minute}-${second}`;
    const filename = `${title}_${timestamp}.png`;

    // Add a timestamp element to the target element
    const timestampElement = document.createElement('div');
    timestampElement.textContent =
      `Downloaded on: ` + new Date().toLocaleString(); // Customize the timestamp format as needed
    timestampElement.style.position = 'absolute';
    timestampElement.style.bottom = '2px'; // Adjust the bottom position as needed
    timestampElement.style.right = '10px'; // Adjust the right position as needed
    targetElementRef.current.appendChild(timestampElement);

    try {
      const dataUrl = await toPng(targetElementRef.current);

      // Remove the timestamp element after capturing the
      targetElementRef.current.removeChild(timestampElement);

      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error(err);
    }
  }, [data]);

  const endElement = useMemo(
    () => (
      <Row className="g-1 align-items-center justify-content-end">
        {Array.isArray(variant) ? (
          variant.length > 1 && (
            <Col className="col-auto">
              <Form.Select
                className="ps-2 pe-5 py-0"
                value={selectedYear}
                name="leftSide"
                onChange={handleYearChange}
              >
                {variant.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )
        ) : (
          <Col className="col-auto">
            {sortedYears?.length > 1 && (
              <Form.Select
                className="ps-2 pe-5 py-0"
                value={selectedYear}
                name="leftSide"
                onChange={handleYearChange}
              >
                {sortedYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Form.Select>
            )}
          </Col>
        )}
        <Col className="text-center p-0 d-flex align-align-items-center">
          <span className="m-auto px-2"></span>
        </Col>
        {selectedMonth && (
          <Col className="col-auto">
            <Form.Select
              className="ps-2 pe-5 py-0"
              value={selectedMonth}
              name="leftSide"
              onChange={handleMonthChange}
            >
              {variant[selectedYear].map(month => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </Form.Select>
          </Col>
        )}
        <Col className="col-auto">
          <Button
            variant="light"
            size="sm"
            className="border-300 me-1 text-600"
            onClick={onButtonClick}
          >
            <img
              src={cloudDownload}
              alt="Download"
              width={15}
              height={'auto'}
            />
          </Button>
        </Col>
      </Row>
    ),
    [
      variant,
      selectedYear,
      selectedMonth,
      sortedYears,
      handleYearChange,
      handleMonthChange
    ]
  );

  const getSelectedYearMonth = () => {
    if (selectedMonth && selectedYear) {
      return `${selectedYear}::${selectedMonth}`;
    }
    return selectedYear;
  };

  const selectedYearMonth = getSelectedYearMonth();

  // console.log(
  //   data,
  //   data[selectedYearMonth],
  //   selectedYearMonth,
  //   label,
  //   'data[selectedYearMonth]'
  // );
  return (
    <Card
      ref={targetElementRef}
      className={classNames('h-100', 'overflow-hidden')}
    >
      <FalconCardHeader
        title={label}
        titleTag="h5"
        light={true}
        desc={data[0]?.desc}
        endEl={endElement}
        infoBtn={!isEmpty(info) && <FiInfo onClick={handleShowModal} />}
      />
      <Card.Body className="p-0">
        {!isEmpty(data[selectedYearMonth]) && (
          <LeafletMap
            data={{
              coordinates: coordinates,
              mapData: data[selectedYearMonth],
              legendData: legendData,
              type: type,
              label: label
            }}
            className="h-100 bg-white"
            style={{ minHeight: '410px' }}
          />
        )}
      </Card.Body>
      <Card.Footer className="bg-white py-2 card-footer-style">
        <h5 className="infrence-info">{data[selectedYearMonth]?.info}</h5>
      </Card.Footer>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={info || {}}
        title={label}
      />
    </Card>
  );
};

export default MultiYearMap;
